import { useNavigate, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import ToolBar from '@/components/ToolBar';
import { useContext, useEffect, useState } from 'react';
import { ClientDetails } from '@pages/clients/ClientDetails';
import AlertBox from '@/components/AlertBox';
import { convertISODateToMMDDYYYY, formatPhoneNumber } from '@/lib/utils';
import { getClients } from '@/services/Network';
import { ClientResponse, useUpdateClientStatus } from '@brainvaulttech/dgr-react-sdk';
import { toast } from '@/components/ui/use-toast';
import { Switch } from '@/components/ui/switch';
import { useUserStore } from '@/stores/user';
import { DataTable } from '@/components/DataTable';
import { OneLineText } from '@/components/OneLineText';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { ExportClientDialog } from './ExportClientDialog';
import { hasFeature } from '@/services/featureService';

const Clients = ({ displayType = 'PAGE' }: { displayType?: 'MODAL' | 'PAGE' }) => {
  const [, setSearchParam] = useSearchParams();
  const navigate = useNavigate();
  const [selectedClientId, setSelectedClientId] = useState<string | null>('');
  const [displayExportDialog, setDisplayExportDialog] = useState(false);
  const role = useUserStore((state) => state.user?.roleId);
  const { setIsDirty } = useContext(NavigationBlockerContext);
  const handleRowClick = (row: ClientResponse, e: React.MouseEvent) => {
    const isButton = (e.target as HTMLElement).closest('button');
    if (!isButton && !(e.target as HTMLElement).classList.contains('modal')) {
      setSearchParam({ clientId: row.clientId });
      setSelectedClientId(row.clientId);
    }
  };

  const { mutate: updateClientStatus, status } = useUpdateClientStatus();
  const columns: ColumnDef<ClientResponse>[] = [
    {
      accessorKey: 'code',
      header: 'Client ID',
      size: role === 'ADMIN' ? 180 : 50,
      cell: ({ cell, row }) => {
        const code = cell.getValue() as number;
        return (
          <div className={`w-full h-full flex flex-col ${role === 'ADMIN' ? 'min-w-[200px]' : 'min-w-[100px]'}`}>
            {code}
            {role === 'ADMIN' && row.original.createdAt && (
              <span className="text-xs text-gray-500">{`Created on: ${convertISODateToMMDDYYYY(row.original.createdAt)}`}</span>
            )}
            {role === 'ADMIN' && row.original.deactivatedAt && (
              <span className="text-xs text-error">{`Deactivated on: ${convertISODateToMMDDYYYY(row.original.deactivatedAt)}`}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'clientName',
      header: 'Client Name',
      enableSorting: true,
      size: 300,
      cell: ({ cell }) => {
        return <OneLineText text={cell.row.original.clientName ?? ''} className="min-w-[200px] flex mr-5" />;
      },
    },
    {
      accessorKey: 'address1',
      header: 'Address',
      size: 200,
      cell: ({ cell }) => {
        return (
          <OneLineText
            text={cell.row.original.address1 + (cell.row.original.address2 ? ', ' + cell.row.original.address2 : '')}
            className="min-w-[150px] mr-5"
          />
        );
      },
    },
    {
      accessorKey: 'city',
      header: 'City',
      enableSorting: true,
      cell: ({ cell }) => {
        return <OneLineText text={cell.row.original.city ?? ''} className="min-w-[100px] mr-5" />;
      },
    },
    {
      accessorKey: 'state',
      header: 'State',
      enableSorting: true,
      cell: ({ cell }) => {
        return <div className="min-w-[100px]">{cell.row.original.state}</div>;
      },
    },
    {
      accessorKey: 'zipCode',
      header: 'Zip Code',
      cell: ({ cell }) => {
        return <div className="min-w-[100px]">{cell.row.original.zipCode}</div>;
      },
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ cell }) => {
        const phone = cell.getValue() as string;
        return <div className="min-w-[150px]">{phone ? formatPhoneNumber(phone) : '-'}</div>;
      },
    },
    ...(role === 'ADMIN'
      ? [
          {
            accessorKey: 'active',
            header: 'Is Active',
            size: 40,
            cell: ({ row: { original } }: { row: { original: ClientResponse } }) => {
              const clientId = original.clientId;
              const clientName = original.clientName;
              const [showDialog, setShowDialog] = useState(false);
              const [checked, setChecked] = useState(original.deactivatedAt ? false : true);

              const handleSwitchChange = (checked: boolean) => {
                setChecked(checked);
                setShowDialog(true);
              };

              const handleDialogYes = () => {
                updateClientStatus(
                  { id: clientId, data: { status: checked } },
                  {
                    onSuccess: (data) => {
                      original.deactivatedAt = data.data.deactivatedAt;
                      setShowDialog(false);
                      toast({
                        variant: 'default',
                        description: `${clientName} has been ${checked ? 'activated' : 'deactivated'}`,
                        duration: 3000,
                      });
                    },
                    onError: () => {
                      setShowDialog(false);
                      toast({
                        variant: 'destructive',
                        description: 'Failed to edit',
                        duration: 3000,
                      });
                    },
                  }
                );
              };

              const handleDialogNo = () => {
                setShowDialog(false);
                setChecked(original.deactivatedAt ? false : true);
              };

              return (
                <div className="w-full h-full min-w-[100px] pr-10">
                  {role === 'ADMIN' ? (
                    <>
                      <AlertBox
                        open={showDialog}
                        title="Are you sure?"
                        description={
                          checked
                            ? `Activating the client will grant access.`
                            : `Deactivating ${clientName} will remove access, but the data will remain stored in the database.`
                        }
                        cancelBtnLabel="Cancel"
                        confirmBtnLabel={checked ? 'Activate' : 'Deactivate'}
                        cancelBtnClick={handleDialogNo}
                        confirmBtnClick={handleDialogYes}
                        warning={checked ? false : true}
                        isLoading={status === 'pending'}
                        triggerButton={
                          <Switch
                            key={checked.toString()}
                            defaultChecked={checked}
                            onCheckedChange={(e) => {
                              handleSwitchChange(e);
                            }}
                            className={`${!checked ? 'bg-input' : 'bg-green-600'}`}
                          />
                        }
                      />
                    </>
                  ) : null}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  useEffect(() => {
    setIsDirty(false);
  }, []);
  return (
    <>
      {displayType === 'PAGE' && (
        <ToolBar
          title="Client Master List"
          buttonTitle="Add Client"
          createButton={hasFeature('client.onboarding.createForm.displayButton')}
          additionalButton={
            <Button
              variant="outline"
              className=" bg-[#FFFFFF] text-primary w-[84px] h-[36px] border-primary hover:bg-[#EDF5FF] p-3"
              onClick={() => setDisplayExportDialog(true)}
            >
              <div className="flex flex-row items-center gap-2">
                <Download className="h-4 w-4" />
                <span className="font-medium hidden sm:block">Export</span>
              </div>
            </Button>
          }
          handleClick={() => navigate('/clients/create')}
        />
      )}
      <div className="flex w-[100%]">
        <DataTable
          columns={columns}
          apiGet={getClients}
          onRowClick={handleRowClick}
          className={displayType === 'MODAL' ? 'max-h-[44vh] min-h-[10px] h-fit scrollbar-hide' : ''}
          {...(displayType === 'PAGE' && {
            search: [
              {
                label: 'Search Client by ID',
                name: 'code',
                placeholder: 'Search Client by ID',
              },
              {
                label: 'Search Client by Name',
                name: 'name',
                placeholder: 'Search Client by Name',
              },
            ],
          })}
        />
      </div>

      {selectedClientId && (
        <ClientDetails
          open={true}
          onCloseClick={() => {
            setSelectedClientId(null);
            setSearchParam({});
          }}
          clientId={selectedClientId}
          role={role!}
        />
      )}
      {displayExportDialog && <ExportClientDialog onClose={() => setDisplayExportDialog(false)} />}
    </>
  );
};

export default Clients;
